import { UserProfile, ClubType, GolfClub } from "../types";
import { estimateClubDistances } from "./distanceCalculator";

const STORAGE_KEY = "clubiq-user-profile";

export const loadUserProfile = (): UserProfile | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const profile = JSON.parse(stored);
      // Convert date strings back to Date objects
      profile.createdAt = new Date(profile.createdAt);
      profile.updatedAt = new Date(profile.updatedAt);

      // Handle migration from old `name` field to `firstName` and `lastName`
      if (profile.name && !profile.firstName && !profile.lastName) {
        const nameParts = profile.name.trim().split(' ');
        if (nameParts.length >= 2) {
          profile.firstName = nameParts[0];
          profile.lastName = nameParts.slice(1).join(' ');
        } else {
          profile.firstName = profile.name;
          profile.lastName = '';
        }
        delete profile.name;
      }

      // Add email field if it doesn't exist
      if (!profile.email) {
        profile.email = '';
        saveUserProfile(profile); // Save the migrated data
      }

      return profile;
    }
  } catch (error) {
    console.error("Error loading user profile:", error);
  }
  return null;
};

export const saveUserProfile = (profile: UserProfile): void => {
  try {
    profile.updatedAt = new Date();
    localStorage.setItem(STORAGE_KEY, JSON.stringify(profile));
  } catch (error) {
    console.error("Error saving user profile:", error);
  }
};

export const createDefaultProfile = (): UserProfile => {
  const now = new Date();
  return {
    id: generateId(),
    firstName: "",
    lastName: "",
    email: "",
    handicap: 18,
    height: 70, // 5'10"
    weight: 180,
    age: 35,
    // Swing speed is now optional - no default assumption
    swingSpeed: undefined,
    swingSpeedRange: undefined,
    swingTempo: "medium",
    ballFlight: "straight",
    skillLevel: "intermediate",
    playingExperience: "casual", // more approachable default
    courseFrequency: "monthly",
    missPattern: undefined,
    preferredCourseType: undefined,
    golfGoals: [], // what they want to improve
    currentIssues: [],
    clubDistances: [],
    golfBag: createDefaultGolfBag(),
    preferredUnits: "yards",
    hasCompletedOnboarding: false,
    createdAt: now,
    updatedAt: now,
  };
};

export const createDefaultGolfBag = (): GolfClub[] => {
  const defaultClubs: Omit<GolfClub, 'id'>[] = [
    { type: "driver" as ClubType, brand: "TaylorMade", model: "SIM2 Max", shaftType: "graphite", shaftFlex: "regular", lie: 58, loft: 10.5, length: 45.75, isInBag: true },
    { type: "3-wood" as ClubType, brand: "Callaway", model: "Epic Speed", shaftType: "graphite", shaftFlex: "regular", lie: 57, loft: 15, length: 43, isInBag: true },
    { type: "5-wood" as ClubType, brand: "Titleist", model: "TSi2", shaftType: "graphite", shaftFlex: "regular", lie: 57.5, loft: 18, length: 42, isInBag: true },
    { type: "3-hybrid" as ClubType, brand: "Ping", model: "G425", shaftType: "graphite", shaftFlex: "regular", lie: 59, loft: 19, length: 40.25, isInBag: true },
    { type: "4-iron" as ClubType, brand: "Mizuno", model: "JPX921 Forged", shaftType: "steel", shaftFlex: "regular", lie: 61, loft: 22, length: 38.5, isInBag: true },
    { type: "5-iron" as ClubType, brand: "Mizuno", model: "JPX921 Forged", shaftType: "steel", shaftFlex: "regular", lie: 61.5, loft: 25, length: 38, isInBag: true },
    { type: "6-iron" as ClubType, brand: "Mizuno", model: "JPX921 Forged", shaftType: "steel", shaftFlex: "regular", lie: 62, loft: 28, length: 37.5, isInBag: true },
    { type: "7-iron" as ClubType, brand: "Mizuno", model: "JPX921 Forged", shaftType: "steel", shaftFlex: "regular", lie: 62.5, loft: 32, length: 37, isInBag: true },
    { type: "8-iron" as ClubType, brand: "Mizuno", model: "JPX921 Forged", shaftType: "steel", shaftFlex: "regular", lie: 63, loft: 36, length: 36.5, isInBag: true },
    { type: "9-iron" as ClubType, brand: "Mizuno", model: "JPX921 Forged", shaftType: "steel", shaftFlex: "regular", lie: 63.5, loft: 41, length: 36, isInBag: true },
    { type: "pw" as ClubType, brand: "Mizuno", model: "JPX921 Forged", shaftType: "steel", shaftFlex: "regular", lie: 64, loft: 46, length: 35.5, isInBag: true },
    { type: "gw" as ClubType, brand: "Cleveland", model: "RTX ZipCore", shaftType: "steel", shaftFlex: "regular", lie: 64, loft: 52, length: 35.25, isInBag: true },
    { type: "sw" as ClubType, brand: "Cleveland", model: "RTX ZipCore", shaftType: "steel", shaftFlex: "regular", lie: 64, loft: 56, length: 35.25, isInBag: true },
    { type: "lw" as ClubType, brand: "Cleveland", model: "RTX ZipCore", shaftType: "steel", shaftFlex: "regular", lie: 64, loft: 60, length: 35, isInBag: true },
  ];

  return defaultClubs.map((club) => ({
    id: generateId(),
    ...club,
  }));
};

const generateId = (): string => {
  return Date.now().toString(36) + Math.random().toString(36).substr(2);
};

// Update club distances based on user profile
export const updateClubDistances = (profile: UserProfile): UserProfile => {
  const estimatedDistances = estimateClubDistances(profile);
  
  const updatedGolfBag = profile.golfBag.map(club => ({
    ...club,
    // Only update if no distance is set, or if it's currently estimated
    distance: club.distance && !club.isDistanceEstimated 
      ? club.distance // Keep user-measured distances
      : estimatedDistances[club.type as ClubType] || club.distance,
    isDistanceEstimated: club.distance && !club.isDistanceEstimated 
      ? false // Keep user-measured flag
      : true // Mark as estimated
  }));

  return {
    ...profile,
    golfBag: updatedGolfBag
  };
};
